// src/annihilation/zones/whisperingGrove.js
// ============================================================
//  ANNIHILATION — Outskirts North: The Whispering Grove (Batch 6).
//  Source of truth: ANNIHILATION_WORLD_ATLAS Part 3 (biome,
//  roster, named locations), ANNIHILATION_SYSTEMS_SPEC Parts
//  4–5 (XP yields, roles, AI archetypes), Part 7 (gathering).
//
//  THE FIRST REAL FIELD. A pale autumn forest of silver birch —
//  wind, fallen leaves, distant crows. Three regions rising
//  west → east, the Silent Hollow to breathe in, and the Fallen
//  Gate at the far end: the way down into the Sunken Fold.
//
//  ARCHITECTURE (the Batch 5 hand-off, honored exactly):
//  · usePlayerController is the player. This file NEVER
//    re-implements movement, Lyt, dodge, or damage.
//  · This file is the ONE owner of its enemy state. Enemies are
//    built by enemyTaxonomy's buildEnemy (roles scale to the
//    REGION's fixed zone level — fixed zones, no level sync).
//  · Bleed (both directions) uses the ONE ledger: enemies via
//    applyBleedStack/tickBleed, the player via takeDotTick
//    (DoT bypasses Lyf — spec law, one entry point).
//  · Awards route through profileWrites only: deltaXp,
//    deltaBloodMoney, deltaMaterial, markZoneVisited.
//
//  All visuals are generated (Batch 4.5 doctrine — "generate if
//  you have to"); real art auto-covers nothing here yet because
//  nothing here has art. Numbers marked "dial" are tuning
//  surface, not law.
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  usePlayerAnimation,
  MobileDpad,
  MobileActionPad,
} from "./PlayerEntity";
// Being continuity (July 16): the player renders AS their being —
// same body here as the Old Fold, the Fold below, and the Yard.
import { AnnihilationPlayer, getBeingPalette } from "./BeingSprite";
// The visual makeover (July 19): every move has its own strike —
// yours by being, theirs by ability. One registry: AbilityFX.js.
import { useAbilityFX, AbilityFXLayer } from "./AbilityFX";
import {
  usePlayerController,
  applyBleedStack,
  tickBleed,
  BLEED,
  MAX_BLEED_STACKS,
} from "./PlayerController";
import SkillBar from "./SkillBar";
import {
  ENEMY_DEFS,
  buildEnemy,
  xpForKill,
  BLOOD_MONEY_DROP,
} from "./enemyTaxonomy";
import {
  deltaXp,
  deltaBloodMoney,
  deltaMaterial,
  markZoneVisited,
} from "./profileWrites";
import { xpFor } from "./stats";
// ── THE 3D SWAP (ie-3d-conversion) ───────────────────────────
// WhisperingGrove3D is purely presentational: this file keeps
// owning enemy AI, gathering, combat, and the controller; the 3D
// view just draws what it's handed. Flip the flag to false and
// the shipped 2D grove returns untouched.
import WhisperingGrove3D from "./WhisperingGrove3D";
const GROVE_3D = true; // rollback switch

// ------------------------------------------------------------
// THE ZONE — identity + fixed levels (fixed zones are law;
// region levels within the Atlas's 1–15 band are dials).
// ------------------------------------------------------------
export const ZONE = {
  id: "whispering_grove",
  name: "The Whispering Grove",
  ring: "The Outskirts",
  level: 5, // canonical zone level for zone-scoped yields (dial)
};

const REGIONS = [
  { id: "fringe", name: "The Fringe", minX: 0, maxX: 8, zoneLevel: 2 },
  { id: "deepwood", name: "The Deepwood", minX: 9, maxX: 17, zoneLevel: 5 },
  { id: "fallen_gate", name: "The Fallen Gate", minX: 18, maxX: 27, zoneLevel: 8 },
];
const regionAt = (x) =>
  REGIONS.find((r) => x >= r.minX && x <= r.maxX) || REGIONS[0];

// ---- geometry (tiles) ----------------------------------------
const TILE = 48;
const BOUNDS = { minX: 1, maxX: 26, minY: 1, maxY: 14 };
const WORLD_W = (BOUNDS.maxX + 2) * TILE;
const WORLD_H = (BOUNDS.maxY + 2) * TILE;
const VIEW_H = 440;

// The Silent Hollow — safe clearing, Atlas-named. No spawns, no
// aggro inside; enemies that lose you here go home.
const HOLLOW = { x: 12, y: 4, r: 2.6 };
const inHollow = (p) => Math.hypot(p.x - HOLLOW.x, p.y - HOLLOW.y) <= HOLLOW.r;

// West exit (back to the Old Fold) and the dungeon mouth.
const VILLAGE_EXIT = { x: 1, y: 8 };
const DUNGEON_DOOR = { x: 25, y: 8 };
const INTERACT_RANGE = 1.7;

// Birch stands — obstacles. Generated visuals; kept off paths.
const TREES = [
  { x: 3, y: 3 }, { x: 4, y: 11 }, { x: 5, y: 6 }, { x: 6, y: 2 },
  { x: 7, y: 12 }, { x: 8, y: 5 }, { x: 9, y: 9 }, { x: 10, y: 2 },
  { x: 10, y: 12 }, { x: 11, y: 7 }, { x: 13, y: 10 }, { x: 14, y: 2 },
  { x: 15, y: 6 }, { x: 15, y: 13 }, { x: 16, y: 3 }, { x: 17, y: 9 },
  { x: 18, y: 4 }, { x: 19, y: 12 }, { x: 20, y: 7 }, { x: 21, y: 2 },
  { x: 21, y: 10 }, { x: 22, y: 5 }, { x: 23, y: 12 }, { x: 24, y: 3 },
];
const TREE_SET = new Set(TREES.map((t) => `${t.x},${t.y}`));

// ------------------------------------------------------------
// SPAWN TABLE — Atlas enemy mix: 6–10 Mooks (Ash Wolves), 2–4
// Skirmishers (Grove Foxes), 1–2 Brutes (Corrupted Elk), 1
// Elite (The Antlered One, a patrol). Region decides the level.
// ------------------------------------------------------------
const SPAWNS = [
  // Fringe — the first wolves (zone level 2)
  { defId: "ash_wolf", pos: { x: 4, y: 4 } },
  { defId: "ash_wolf", pos: { x: 6, y: 9 } },
  { defId: "ash_wolf", pos: { x: 7, y: 6 } },
  // Deepwood (zone level 5)
  { defId: "ash_wolf", pos: { x: 10, y: 10 } },
  { defId: "ash_wolf", pos: { x: 13, y: 8 } },
  { defId: "ash_wolf", pos: { x: 16, y: 11 } },
  { defId: "grove_fox", pos: { x: 11, y: 12 } },
  { defId: "grove_fox", pos: { x: 15, y: 3 } },
  { defId: "corrupted_elk", pos: { x: 14, y: 12 } },
  // The Fallen Gate approach (zone level 8)
  { defId: "ash_wolf", pos: { x: 19, y: 5 } },
  { defId: "ash_wolf", pos: { x: 22, y: 11 } },
  { defId: "grove_fox", pos: { x: 20, y: 3 } },
  { defId: "corrupted_elk", pos: { x: 22, y: 8 } },
];

// The Antlered One — one alive at a time, walking its round.
const ELITE_WAYPOINTS = [
  { x: 19, y: 9 }, { x: 23, y: 5 }, { x: 25, y: 11 }, { x: 20, y: 13 },
];

// ------------------------------------------------------------
// GATHERING — Spec Part 7. Proximity + interact, no combat.
// Respawn on a rotation (10–20 min law; 10 min dial here).
// ------------------------------------------------------------
export const MATERIALS = {
  ashen_moss: { id: "ashen_moss", name: "Ashen Moss", rarity: "common" },
  fold_iron: { id: "fold_iron", name: "Fold-Iron", rarity: "uncommon" },
};
const GATHER_RESPAWN_MS = 10 * 60 * 1000; // dial (law: 10–20 min)

// One stable geometry object for the 3D renderer — same numbers
// the 2D grove draws from, handed down so there's one source.
const GROVE_GEO = {
  bounds: BOUNDS, regions: REGIONS, regionAt,
  trees: TREES, hollow: HOLLOW,
  villageExit: VILLAGE_EXIT, dungeonDoor: DUNGEON_DOOR,
  materials: MATERIALS,
};
const NODE_SPOTS = [
  { id: "moss_1", materialId: "ashen_moss", pos: { x: 3, y: 7 } },
  { id: "moss_2", materialId: "ashen_moss", pos: { x: 8, y: 11 } },
  { id: "moss_3", materialId: "ashen_moss", pos: { x: 13, y: 5 } },
  { id: "moss_4", materialId: "ashen_moss", pos: { x: 18, y: 10 } },
  { id: "moss_5", materialId: "ashen_moss", pos: { x: 24, y: 6 } },
  { id: "iron_1", materialId: "fold_iron", pos: { x: 9, y: 3 } },
  { id: "iron_2", materialId: "fold_iron", pos: { x: 21, y: 12 } },
];

// ---- AI feel dials -------------------------------------------
const AI_TICK_MS = 170;
const RESPAWN_MS = 30000; //        field enemies (dial)
const ELITE_RESPAWN_MS = 5 * 60 * 1000; // the patrol returns (dial)
const DEAGGRO_DIST = 9;
const KIT = {
  ash_wolf: { aggro: 5, range: 1.5, moveMs: 260, windMs: 650, recoverMs: 550 },
  grove_fox: { aggro: 6, range: 1.6, moveMs: 190, windMs: 320, recoverMs: 400, band: [3, 5], dartMs: 2800, fleeMs: 1300 },
  corrupted_elk: { aggro: 4.5, range: 1.9, moveMs: 520, windMs: 1200, recoverMs: 800 },
  the_antlered_one: { sight: 7, range: 1.9, moveMs: 300, windMs: 900, recoverMs: 700, chargeTiles: 5, chargeStepMs: 90 },
  default: { aggro: 5, range: 1.5, moveMs: 300, windMs: 700, recoverMs: 600 },
};
const kitFor = (defId) => KIT[defId] || KIT.default;

let floatSeq = 0;
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const stepToward = (from, to) => ({
  x: from.x + Math.sign(to.x - from.x),
  y: from.y + Math.sign(to.y - from.y),
});

export default function WhisperingGrove({
  uid,
  character,
  onBack, //          ← the Gate (village side)
  onEnterDungeon, //  ← the Sunken Fold
  onDown, //          ← death law: respawn at the village
}) {
  // ---- enemy state (this file is its one owner) --------------
  const [enemies, setEnemies] = useState(() => [
    ...SPAWNS.map((s) =>
      buildEnemy(ENEMY_DEFS[s.defId], regionAt(s.pos.x).zoneLevel, s.pos)
    ),
    (() => {
      const e = buildEnemy(
        ENEMY_DEFS.the_antlered_one,
        regionAt(ELITE_WAYPOINTS[0].x).zoneLevel,
        ELITE_WAYPOINTS[0]
      );
      e.waypoint = 0;
      return e;
    })(),
  ]);
  const enemiesRef = useRef(enemies);
  useEffect(() => {
    enemiesRef.current = enemies;
  }, [enemies]);

  const [nodes, setNodes] = useState(() =>
    NODE_SPOTS.map((n) => ({ ...n, readyAt: 0 }))
  );
  const nodesRef = useRef(nodes);
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  const [floats, setFloats] = useState([]);
  const [feltEvent, setFeltEvent] = useState(null);
  const [playerBleed, setPlayerBleed] = useState({ stacks: 0, expiresAt: 0 });
  const [downed, setDowned] = useState(false);
  const downedRef = useRef(false);

  const addFloat = useCallback((pos, text, color) => {
    const id = ++floatSeq;
    setFloats((f) => [...f, { id, x: pos.x, y: pos.y, text, color }]);
    setTimeout(() => setFloats((f) => f.filter((x) => x.id !== id)), 950);
  }, []);

  const flash = useCallback((text) => {
    setFeltEvent(text);
    setTimeout(() => setFeltEvent(null), 1100);
  }, []);

  // ---- award pipes (fire-and-forget; the lane is the truth) ---
  const awardKill = useCallback(
    (enemy) => {
      const xp = xpForKill(enemy.role, enemy.zoneLevel);
      const bm = BLOOD_MONEY_DROP[enemy.role] || 0;
      addFloat(enemy.pos, `+${xp} xp`, "#8fd18a");
      if (bm) addFloat({ x: enemy.pos.x, y: enemy.pos.y + 0.7 }, `+${bm} 🩸`, "#e86a5c");
      if (uid) {
        deltaXp(uid, xp).catch(() => {});
        if (bm) deltaBloodMoney(uid, bm).catch(() => {});
      }
    },
    [uid, addFloat]
  );

  // ---- first footsteps — discovery bonus (Spec Part 4) --------
  useEffect(() => {
    if (!uid) return;
    markZoneVisited(uid, ZONE.id)
      .then(({ firstVisit }) => {
        if (!firstVisit) return;
        const xp = xpFor("firstTimeInZone", ZONE.level);
        deltaXp(uid, xp).catch(() => {});
        flash(`${ZONE.name} discovered — +${xp} xp`);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // ---- ability FX (one registry, AbilityFX.js) -----------------
  const { fx, spawnFx } = useAbilityFX();
  const pal = getBeingPalette(character?.beingId);

  // ---- the controller (the overseer) --------------------------
  const ctrl = usePlayerController({
    character,
    startPos: { x: 2, y: 8 },
    bounds: BOUNDS,
    isBlocked: (p) => {
      // v4 FREE-MOVE: position is continuous — block by radius,
      // not tile identity. Trees occupy their tile center; check
      // the 3×3 neighborhood around the rounded position.
      const rx = Math.round(p.x), ry = Math.round(p.y);
      for (let ox = -1; ox <= 1; ox++) {
        for (let oy = -1; oy <= 1; oy++) {
          if (
            TREE_SET.has(`${rx + ox},${ry + oy}`) &&
            Math.hypot(p.x - (rx + ox), p.y - (ry + oy)) < 0.62
          ) return true;
        }
      }
      return enemiesRef.current.some(
        (e) => e.alive && Math.hypot(e.pos.x - p.x, e.pos.y - p.y) < 0.55
      );
    },
    getTargets: () => enemiesRef.current,
    onHit: ({ targetId, damage, crit, bleedApplied }) => {
      const struck = enemiesRef.current.find((e) => e.id === targetId);
      if (struck) spawnFx("hit_spark", { pos: struck.pos, crit, tint: pal });
      setEnemies((list) =>
        list.map((e) => {
          if (e.id !== targetId || !e.alive) return e;
          // Skirmisher law: dodges player abilities (not mid-swing)
          const def = ENEMY_DEFS[e.defId];
          if (
            def?.dodgeChance &&
            e.state !== "telegraph" &&
            Math.random() < def.dodgeChance
          ) {
            addFloat(e.pos, "evaded", "#9fb4c9");
            return e;
          }
          let next = { ...e, anima: Math.max(0, e.anima - damage) };
          if (bleedApplied) next = applyBleedStack(next);
          addFloat(e.pos, crit ? `${damage}!` : `${damage}`, crit ? "#ffd24a" : "#e86a5c");
          if (next.anima <= 0) {
            next.alive = false;
            next.telegraphing = false;
            next.respawnAt =
              Date.now() + (def?.unique ? ELITE_RESPAWN_MS : RESPAWN_MS);
            awardKill(next);
          }
          return next;
        })
      );
    },
    onEvent: (evt) => {
      if (evt.type === "swing" && evt.lytId) {
        // the ability id IS the registry key (vyren)
        spawnFx(evt.lytId, { pos: evt.origin, facing: evt.facing, step: evt.step, tint: pal });
      }
      if (evt.type === "dodge") {
        spawnFx("dodge_streak", { pos: evt.from, to: evt.to, tint: pal });
      }
      if (evt.type === "dry") {
        spawnFx("lyt_dry", { pos: evt.origin });
        flash("Lyt spent — breathe");
      }
      // FX-1 — the cast pass. The ability id IS the registry key
      // (vyren); Spectral Claws spawns one streak per sought prey,
      // Haunting Presence fires HER key — the Matriarch's own move.
      if (evt.type === "cast") {
        if (evt.abilityId === "spectral_claws" && evt.targets?.length) {
          evt.targets.forEach((t, i) =>
            setTimeout(
              () => spawnFx("spectral_claws", { pos: evt.origin, to: t.pos, tint: pal }),
              i * 60
            )
          );
        } else {
          spawnFx(evt.abilityId, { pos: evt.origin, facing: evt.facing, tint: pal, big: evt.abilityId === "haunting_presence" });
        }
        if (evt.abilityId === "spectral_paws") flash("Spectral Paws — the next strike hunts the weak");
        if (evt.abilityId === "spectral_claws") flash("Spectral Claws — three shadows loosed");
        if (evt.abilityId === "haunting_presence") flash("HAUNTING PRESENCE — the Grove goes monochrome");
        if (evt.abilityId === "ghost_form") flash("Ghost Form — between worlds, 8 seconds");
        if (evt.abilityId === "nine_lives") flash("NINE LIVES — a life spent, the Tiger reforms");
      }
      if (evt.type === "cast_end") flash("the veil closes — Ghost Form ends");
      if (evt.type === "chao_dry") flash("Chao runs thin — let it pool");
      if (evt.type === "cast_locked") {
        const why = { impera: "Impera awakens at Champion (L30)", uhlti: "U'hlti awakens at Lord (L60)", transformation: "Ghost Form is bought at Sigfer's (unlock node)", primera: "Primera awakens after the Transcendence" }[evt.slotId];
        flash(why || "that slot is still dark");
      }
      if (evt.type === "hurt" && evt.dodged) flash("phased!");
      if (evt.type === "swing" && evt.free) flash("Phase Momentum — free swing");
    },
  });

  // PERF (v3): the controller now exposes its own live posRef —
  // updated synchronously inside the movement tick, no effect lag.
  const ctrlPosRef = ctrl.posRef;

  // ---- death law: respawn at the village ----------------------
  useEffect(() => {
    if (downedRef.current || ctrl.anima > 0) return;
    downedRef.current = true;
    setDowned(true);
    setTimeout(() => onDown?.(), 1900);
  }, [ctrl.anima, onDown]);

  // ---- an enemy hit landing on the player ----------------------
  const enemyStrikes = useCallback(
    (enemy) => {
      if (downedRef.current) return;
      const res = ctrl.takeHit({ attack: enemy.attack });
      addFloat(
        ctrlPosRef.current,
        res.dodged ? "phase" : `-${res.damage}`,
        res.dodged ? "#8fd18a" : "#e86a5c"
      );
      const def = ENEMY_DEFS[enemy.defId];
      if (!res.dodged && def?.appliesBleed) {
        setPlayerBleed((b) => ({
          stacks: Math.min(MAX_BLEED_STACKS, b.stacks + 1),
          expiresAt: Date.now() + BLEED.durationSec * 1000,
        }));
        flash("bleeding — it goes through Lyf");
      }
    },
    [ctrl, addFloat, flash]
  );

  // ---- player bleed ticks (DoT bypasses Lyf — one entry point) -
  useEffect(() => {
    const t = setInterval(() => {
      setPlayerBleed((b) => {
        if (!b.stacks) return b;
        if (Date.now() > b.expiresAt) return { stacks: 0, expiresAt: 0 };
        const dmg = ctrl.takeDotTick(BLEED.dps * b.stacks);
        addFloat(ctrlPosRef.current, `-${dmg}`, "#c95c5c");
        return b;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- enemy bleed ticks — the ONE ledger ----------------------
  useEffect(() => {
    const t = setInterval(() => {
      setEnemies((list) => {
        // PERF (v3): identity bail — no bleeders, no re-render.
        if (!list.some((e) => e.alive && e.bleedStacks)) return list;
        return list.map((e) => {
          if (!e.alive || !e.bleedStacks) return e;
          const { enemy, damage } = tickBleed(e);
          if (damage > 0) addFloat(e.pos, `${damage}`, "#c95c5c");
          if (enemy.anima <= 0) {
            const def = ENEMY_DEFS[e.defId];
            const dead = {
              ...enemy,
              alive: false,
              telegraphing: false,
              respawnAt:
                Date.now() + (def?.unique ? ELITE_RESPAWN_MS : RESPAWN_MS),
            };
            awardKill(dead);
            return dead;
          }
          return enemy;
        });
      });
    }, 1000);
    return () => clearInterval(t);
  }, [addFloat, awardKill]);

  // ---- THE AI TICK — five archetypes, one clock ----------------
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      const player = ctrlPosRef.current;
      const playerSafe = inHollow(player);
      setEnemies((list) => {
        // PERF (v3): identity bail — a calm grove must not re-render
        // the 3D scene 6× a second. If every enemy maps to itself,
        // return the SAME array and React skips the whole commit.
        let changed = false;
        const next = list.map((e) => {
          const def = ENEMY_DEFS[e.defId];
          const kit = kitFor(e.defId);

          // respawn watch
          if (!e.alive) {
            if (e.respawnAt && now >= e.respawnAt) {
              const fresh = buildEnemy(def, e.zoneLevel, e.spawnPos);
              if (def?.unique) fresh.waypoint = 0;
              return fresh;
            }
            return e;
          }
          if (e.telegraphing || now < e.stateUntil) return e;
          const d = dist(e.pos, player);

          // The Silent Hollow — nothing hunts you there.
          if (playerSafe && e.state !== "idle") {
            const home = stepToward(e.pos, e.spawnPos);
            return { ...e, state: "idle", pos: home };
          }

          // --- PREDATOR (the patrol elite) ----------------------
          if (e.ai === "predator") {
            if (e.state === "idle") {
              if (!playerSafe && d <= kit.sight) {
                // the charge — telegraph, then a straight run
                setTimeout(() => {
                  chargeAt(e.id, kit);
                }, kit.windMs);
                return { ...e, state: "telegraph", telegraphing: true, stateUntil: now + kit.windMs };
              }
              // walk the round
              const wp = ELITE_WAYPOINTS[e.waypoint || 0];
              if (dist(e.pos, wp) < 0.8) {
                return { ...e, waypoint: ((e.waypoint || 0) + 1) % ELITE_WAYPOINTS.length };
              }
              return { ...e, pos: stepToward(e.pos, wp), stateUntil: now + kit.moveMs };
            }
            if (e.state === "aggro") {
              if (d > 12) return { ...e, state: "idle" };
              if (d <= kit.range) {
                setTimeout(() => resolveSwing(e.id, kit.range), kit.windMs);
                return { ...e, state: "telegraph", telegraphing: true, stateUntil: now + kit.windMs };
              }
              return { ...e, pos: stepToward(e.pos, player), stateUntil: now + kit.moveMs };
            }
            return { ...e, state: "aggro" };
          }

          // --- SKITTISH (the fox) -------------------------------
          if (e.ai === "skittish") {
            if (e.state === "idle") {
              if (d <= kit.aggro && !playerSafe) {
                return { ...e, state: "aggro", dartAt: now + kit.dartMs };
              }
              return e;
            }
            if (e.state === "retreat") {
              const away = {
                x: Math.max(BOUNDS.minX, Math.min(BOUNDS.maxX, e.pos.x + Math.sign(e.pos.x - player.x))),
                y: Math.max(BOUNDS.minY, Math.min(BOUNDS.maxY, e.pos.y + Math.sign(e.pos.y - player.y))),
              };
              return { ...e, pos: away, state: "aggro", dartAt: now + kit.dartMs, stateUntil: now + kit.moveMs };
            }
            // aggro: hold the band, dart when it's time
            if (d > DEAGGRO_DIST) return { ...e, state: "idle" };
            if (now >= (e.dartAt || 0)) {
              if (d <= kit.range + 0.4) {
                setTimeout(() => resolveSwing(e.id, kit.range), kit.windMs);
                return { ...e, state: "telegraph", telegraphing: true, stateUntil: now + kit.windMs };
              }
              return { ...e, pos: stepToward(e.pos, player), stateUntil: now + 110 };
            }
            const [near, far] = kit.band;
            if (d < near) {
              const away = {
                x: Math.max(BOUNDS.minX, Math.min(BOUNDS.maxX, e.pos.x + Math.sign(e.pos.x - player.x))),
                y: Math.max(BOUNDS.minY, Math.min(BOUNDS.maxY, e.pos.y + Math.sign(e.pos.y - player.y))),
              };
              return { ...e, pos: away, stateUntil: now + kit.moveMs };
            }
            if (d > far) {
              return { ...e, pos: stepToward(e.pos, player), stateUntil: now + kit.moveMs };
            }
            return e;
          }

          // --- AGGRESSOR (wolves, elk — the dummy pattern, live) -
          if (e.state === "idle") {
            if (d <= kit.aggro && !playerSafe) return { ...e, state: "aggro" };
            if (dist(e.pos, e.spawnPos) > 0.8) {
              return { ...e, pos: stepToward(e.pos, e.spawnPos), stateUntil: now + kit.moveMs };
            }
            return e;
          }
          if (e.state === "aggro") {
            if (d > DEAGGRO_DIST) return { ...e, state: "idle" };
            if (d <= kit.range) {
              setTimeout(() => resolveSwing(e.id, kit.range), kit.windMs);
              return { ...e, state: "telegraph", telegraphing: true, stateUntil: now + kit.windMs };
            }
            const next = stepToward(e.pos, player);
            if (TREE_SET.has(`${next.x},${next.y}`)) {
              // slide along the block — try the other axis
              const alt = { x: e.pos.x, y: next.y };
              const alt2 = { x: next.x, y: e.pos.y };
              const pick = !TREE_SET.has(`${alt.x},${alt.y}`) ? alt
                : !TREE_SET.has(`${alt2.x},${alt2.y}`) ? alt2 : e.pos;
              return { ...e, pos: pick, stateUntil: now + kit.moveMs };
            }
            return { ...e, pos: next, stateUntil: now + kit.moveMs };
          }
          return { ...e, state: "aggro" };
        });
        for (let i = 0; i < next.length; i++) {
          if (next[i] !== list[i]) { changed = true; break; }
        }
        return changed ? next : list;
      });
    }, AI_TICK_MS);
    return () => clearInterval(t);
    // resolveSwing/chargeAt are stable useCallbacks below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A telegraph resolving — the enemy swings if you're still there.
  const resolveSwing = useCallback(
    (enemyId, range) => {
      // the swing fires whether it lands or not — you SEE what
      // you dodged. Ability decides the visual: beast-line gore
      // (elk / the Antlered One) vs the pack pounce (wolf/fox).
      const striker = enemiesRef.current.find((e) => e.id === enemyId);
      if (striker?.alive) {
        const sDef = ENEMY_DEFS[striker.defId];
        spawnFx(sDef?.appliesBleed ? "gore" : "pounce", {
          pos: striker.pos,
          toward: ctrlPosRef.current,
          big: striker.role === "elite",
        });
      }
      setEnemies((list) =>
        list.map((e) => {
          if (e.id !== enemyId) return e;
          if (!e.alive) return { ...e, telegraphing: false };
          const kit = kitFor(e.defId);
          const after = {
            ...e,
            telegraphing: false,
            state: e.ai === "skittish" ? "retreat" : e.state === "telegraph" ? "aggro" : e.state,
            stateUntil: Date.now() + kit.recoverMs,
          };
          if (dist(e.pos, ctrlPosRef.current) <= range + 0.3) enemyStrikes(e);
          return after;
        })
      );
    },
    [enemyStrikes, spawnFx]
  );

  // The Antlered One's charge — a straight run at where you were.
  const chargeAt = useCallback(
    (enemyId, kit) => {
      const target = { ...ctrlPosRef.current };
      let steps = 0;
      const run = setInterval(() => {
        steps += 1;
        let connected = false;
        setEnemies((list) =>
          list.map((e) => {
            if (e.id !== enemyId || !e.alive) return e;
            const next = stepToward(e.pos, target);
            const landed = TREE_SET.has(`${next.x},${next.y}`) ? e.pos : next;
            if (dist(landed, ctrlPosRef.current) <= 1.2) connected = true;
            return {
              ...e,
              telegraphing: false,
              pos: landed,
              state: "aggro",
              stateUntil: Date.now() + 60,
            };
          })
        );
        if (connected) {
          const elite = enemiesRef.current.find((e) => e.id === enemyId);
          if (elite) {
            // the charge lands — the full antler gore, elite-scaled
            spawnFx("gore", { pos: elite.pos, toward: ctrlPosRef.current, big: true });
            enemyStrikes(elite);
          }
          clearInterval(run);
          return;
        }
        if (steps >= kit.chargeTiles) clearInterval(run);
      }, kit.chargeStepMs);
    },
    [enemyStrikes, spawnFx]
  );

  // ---- INTERACT — gather / descend / go home -------------------
  const interact = useCallback(() => {
    const p = ctrlPosRef.current;
    // the Fallen Gate — down into the Sunken Fold
    if (dist(p, DUNGEON_DOOR) <= INTERACT_RANGE) {
      onEnterDungeon?.();
      return;
    }
    // the west trail home
    if (dist(p, VILLAGE_EXIT) <= INTERACT_RANGE) {
      onBack?.();
      return;
    }
    // gathering (Spec Part 7 — proximity + interact, no combat)
    const now = Date.now();
    const node = nodesRef.current.find(
      (n) => now >= n.readyAt && dist(p, n.pos) <= INTERACT_RANGE
    );
    if (node) {
      const mat = MATERIALS[node.materialId];
      const xp = xpFor("gatherNode", regionAt(node.pos.x).zoneLevel);
      setNodes((list) =>
        list.map((n) =>
          n.id === node.id ? { ...n, readyAt: now + GATHER_RESPAWN_MS } : n
        )
      );
      addFloat(node.pos, `+1 ${mat.name}`, "#a9c9a4");
      if (xp) addFloat({ x: node.pos.x, y: node.pos.y + 0.7 }, `+${xp} xp`, "#8fd18a");
      if (uid) {
        deltaMaterial(uid, mat.id, 1).catch(() => {});
        if (xp) deltaXp(uid, xp).catch(() => {});
      }
    }
  }, [uid, onBack, onEnterDungeon, addFloat]);

  useEffect(() => {
    const down = (e) => {
      if (e.key === "e" || e.key === "E") {
        e.preventDefault();
        interact();
      }
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, [interact]);

  // Is anything interactable in reach? (drives the prompt + pad)
  const nearDoor = dist(ctrl.pos, DUNGEON_DOOR) <= INTERACT_RANGE;
  const nearExit = dist(ctrl.pos, VILLAGE_EXIT) <= INTERACT_RANGE;
  const nearNode = nodes.some(
    (n) => Date.now() >= n.readyAt && dist(ctrl.pos, n.pos) <= INTERACT_RANGE
  );
  const prompt = nearDoor
    ? "E — descend into the Sunken Fold"
    : nearExit
    ? "E — the trail home to the Old Fold"
    : nearNode
    ? "E — gather"
    : null;

  const anim = usePlayerAnimation(ctrl.pos);
  const region = regionAt(ctrl.pos.x);

  // ---- camera — follow, clamped --------------------------------
  const [viewW, setViewW] = useState(
    Math.min(typeof window !== "undefined" ? window.innerWidth - 20 : 720, 720)
  );
  useEffect(() => {
    const onResize = () => setViewW(Math.min(window.innerWidth - 20, 720));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const camX = Math.max(
    0,
    Math.min(WORLD_W - viewW, ctrl.pos.x * TILE - viewW / 2)
  );
  const camY = Math.max(
    0,
    Math.min(WORLD_H - VIEW_H, ctrl.pos.y * TILE - VIEW_H / 2)
  );

  // ---- render ---------------------------------------------------
  if (GROVE_3D) {
    return (
      <>
        <WhisperingGrove3D
          character={character}
          ctrlPos={ctrl.pos}
          ctrlFacing={ctrl.facing}
          ctrlPosRef={ctrl.posRef}
          ctrlFacingRef={ctrl.facingRef}
          onAim={ctrl.setAim}
          dim={ctrl.dodging || ctrl.isInvulnerable()}
          swing={ctrl.lastSwing}
          dodging={ctrl.dodging}
          cast={ctrl.lastCast}
          ghost={ctrl.ghostForm}
          grace={ctrl.graceArmed}
          pawsArmed={ctrl.pawsArmed}
          keysRef={ctrl.keysRef}
          onAttack={ctrl.attack}
          onDodge={ctrl.dodge}
          onInteract={interact}
          enemies={enemies}
          nodes={nodes}
          floats={floats}
          geo={GROVE_GEO}
          prompt={prompt}
          feltEvent={feltEvent}
          downed={downed}
          onBack={onBack}
        />
        {/* HUD — the grove's own bars, floated over the 3D view */}
        <div style={{ ...S.hud, position: "fixed", top: 8, left: "50%",
          transform: "translateX(-50%)", width: "min(560px, calc(100vw - 24px))",
          zIndex: 30 }}>
          <div style={S.hudTitle}>
            {ZONE.name}
            <span style={S.hudRegion}> · {region.name} (lv {region.zoneLevel})</span>
          </div>
          <Bar label="Anima" value={ctrl.anima} max={ctrl.animaMax} color="#e86a5c" />
          <Bar label="Chao" value={ctrl.chao} max={ctrl.chaoMax} color="#8a6cff" />
          <div style={S.lytRow}>
            <span style={S.lytLabel}>Lyt</span>
            {Array.from({ length: ctrl.lytMax }).map((_, i) => (
              <span key={i} style={{ ...S.lytPip, ...(i < Math.floor(ctrl.lyt) ? S.lytPipFull : null) }} />
            ))}
            <span style={S.comboText}>
              {ctrl.comboStep ? ` combo ${ctrl.comboStep}/${ctrl.comboLength}` : ""}
            </span>
            {playerBleed.stacks ? (
              <span style={S.playerBleed}>🩸×{playerBleed.stacks}</span>
            ) : null}
          </div>
          <div style={S.barHolder}>
            <SkillBar character={character} compact />
          </div>
        </div>
      </>
    );
  }

  return (
    <div style={S.void}>
      {/* HUD */}
      <div style={{ ...S.hud, maxWidth: viewW }}>
        <div style={S.hudTitle}>
          {ZONE.name}
          <span style={S.hudRegion}> · {region.name} (lv {region.zoneLevel})</span>
        </div>
        <Bar label="Anima" value={ctrl.anima} max={ctrl.animaMax} color="#e86a5c" />
        <Bar label="Chao" value={ctrl.chao} max={ctrl.chaoMax} color="#8a6cff" />
        <div style={S.lytRow}>
          <span style={S.lytLabel}>Lyt</span>
          {Array.from({ length: ctrl.lytMax }).map((_, i) => (
            <span
              key={i}
              style={{
                ...S.lytPip,
                ...(i < Math.floor(ctrl.lyt) ? S.lytPipFull : null),
              }}
            />
          ))}
          <span style={S.comboText}>
            {ctrl.comboStep ? ` combo ${ctrl.comboStep}/${ctrl.comboLength}` : ""}
          </span>
          {playerBleed.stacks ? (
            <span style={S.playerBleed}>🩸×{playerBleed.stacks}</span>
          ) : null}
        </div>
        <div style={S.barHolder}>
          <SkillBar character={character} compact />
        </div>
      </div>

      {/* the viewport */}
      <div style={{ ...S.viewport, width: viewW, height: VIEW_H }}>
        <div
          style={{
            ...S.world,
            width: WORLD_W,
            height: WORLD_H,
            transform: `translate(${-camX}px, ${-camY}px)`,
          }}
        >
          <GroveGround />

          {/* the Silent Hollow — the safe clearing */}
          <div
            style={{
              ...S.hollow,
              left: (HOLLOW.x - HOLLOW.r) * TILE,
              top: (HOLLOW.y - HOLLOW.r) * TILE,
              width: HOLLOW.r * 2 * TILE,
              height: HOLLOW.r * 2 * TILE,
            }}
          >
            <div style={S.shrine}>⛩</div>
            <div style={S.bench} />
            <div style={S.hollowLabel}>The Silent Hollow</div>
          </div>

          {/* the trail home */}
          <div
            style={{ ...S.exit, left: (VILLAGE_EXIT.x - 0.4) * TILE, top: (VILLAGE_EXIT.y - 1) * TILE }}
          >
            <div style={S.exitArch} />
            <div style={S.exitLabel}>the Old Fold</div>
          </div>

          {/* the Fallen Gate — the dungeon mouth */}
          <div
            style={{ ...S.fallenGate, left: (DUNGEON_DOOR.x - 1) * TILE, top: (DUNGEON_DOOR.y - 1.4) * TILE }}
          >
            <div style={S.gatePostL} />
            <div style={S.gatePostR} />
            <div style={S.gateMaw} />
            <div style={S.gateLabel}>The Fallen Gate</div>
          </div>

          {/* birches */}
          {TREES.map((t, i) => (
            <Birch key={i} x={t.x} y={t.y} />
          ))}

          {/* gathering nodes */}
          {nodes.map((n) => {
            const ready = Date.now() >= n.readyAt;
            const mat = MATERIALS[n.materialId];
            return (
              <div
                key={n.id}
                style={{
                  ...S.node,
                  left: n.pos.x * TILE + 6,
                  top: n.pos.y * TILE + 6,
                  opacity: ready ? 1 : 0.22,
                  ...(mat.id === "fold_iron" ? S.nodeIron : null),
                }}
                title={mat.name}
              >
                {mat.id === "fold_iron" ? "⛏" : "❋"}
              </div>
            );
          })}

          {/* enemies */}
          {enemies.map((e) =>
            e.alive ? (
              <EnemyVisual key={e.id} enemy={e} />
            ) : (
              <div
                key={e.id}
                style={{ ...S.downMark, left: e.pos.x * TILE, top: e.pos.y * TILE }}
              >
                ✦
              </div>
            )
          )}

          {/* ability visuals — the one registry, world-space */}
          <AbilityFXLayer fx={fx} tile={TILE} />

          {/* floating numbers */}
          {floats.map((f) => (
            <div
              key={f.id}
              style={{
                ...S.float,
                left: f.x * TILE + TILE / 2,
                top: f.y * TILE - TILE * 0.4,
                color: f.color,
              }}
            >
              {f.text}
            </div>
          ))}

          {/* the player — their being, with their Terror self trailing */}
          <AnnihilationPlayer
            character={character}
            pos={ctrl.pos}
            facing={ctrl.facing}
            walkFrame={anim.walkFrame}
            isIdle={anim.isIdle}
            idleFrame={anim.idleFrame}
            tile={TILE}
            opacity={ctrl.dodging || ctrl.isInvulnerable() ? 0.45 : 1}
            swing={ctrl.lastSwing}
          />
        </div>
      </div>

      {prompt ? <div style={S.prompt}>{prompt}</div> : null}
      {feltEvent ? <div style={S.felt}>{feltEvent}</div> : null}

      {downed ? (
        <div style={S.downVeil}>
          <div style={S.downText}>the Grove goes quiet</div>
          <div style={S.downSub}>you wake at the Old Fold — nothing is lost, nothing is rested</div>
        </div>
      ) : null}

      <div style={S.controls}>
        move: arrows / WASD · attack: Space or J · dodge: Shift or K · interact: E
      </div>

      <button style={S.back} onClick={onBack}>
        ← back to the Gate
      </button>

      <MobileDpad keysRef={ctrl.keysRef} />
      <MobileActionPad onAttack={ctrl.attack} onDodge={ctrl.dodge} />
      {prompt ? (
        <button style={S.interactPad} onClick={interact}>
          ◆
        </button>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------
// Generated visuals — no assets required (Batch 4.5 doctrine).
// ------------------------------------------------------------
function GroveGround() {
  return (
    <div style={S.ground}>
      {/* fallen-leaf drifts */}
      {Array.from({ length: 40 }).map((_, i) => (
        <div
          key={i}
          style={{
            ...S.leafDrift,
            left: `${(i * 137) % 100}%`,
            top: `${(i * 61) % 100}%`,
            opacity: 0.08 + ((i * 7) % 10) / 90,
          }}
        />
      ))}
    </div>
  );
}

function Birch({ x, y }) {
  return (
    <div style={{ ...S.birch, left: x * TILE, top: (y - 1.2) * TILE }}>
      <div style={S.birchCanopy} />
      <div style={S.birchTrunk}>
        <div style={S.birchScar} />
        <div style={{ ...S.birchScar, top: "62%" }} />
      </div>
    </div>
  );
}

function EnemyVisual({ enemy }) {
  const def = ENEMY_DEFS[enemy.defId] || {};
  const isElite = enemy.role === "elite";
  const body =
    enemy.defId === "grove_fox"
      ? S.foxBody
      : enemy.defId === "corrupted_elk"
      ? S.elkBody
      : isElite
      ? S.eliteBody
      : S.wolfBody;
  return (
    <div
      style={{
        ...S.enemy,
        left: enemy.pos.x * TILE,
        top: enemy.pos.y * TILE - TILE * 0.35,
        ...(enemy.telegraphing ? S.enemyTelegraph : null),
      }}
    >
      <div style={body}>
        {enemy.defId === "corrupted_elk" || isElite ? (
          <div style={{ ...S.antlers, ...(isElite ? S.antlersElite : null) }} />
        ) : (
          <div style={S.ears} />
        )}
        <div style={S.eyes} />
      </div>
      <div style={S.enemyHp}>
        <div
          style={{
            ...S.enemyHpFill,
            width: `${(enemy.anima / enemy.animaMax) * 100}%`,
            ...(isElite ? { background: "#c9a4e8" } : null),
          }}
        />
      </div>
      {isElite ? <div style={S.eliteTag}>◆ {def.name}</div> : null}
      {enemy.bleedStacks ? (
        <div style={S.bleedTag}>
          🩸×{enemy.bleedStacks}
          {enemy.bleedStacks >= MAX_BLEED_STACKS ? " MAX" : ""}
        </div>
      ) : null}
    </div>
  );
}

function Bar({ label, value, max, color }) {
  return (
    <div style={S.statBar}>
      <span style={S.statBarLabel}>{label}</span>
      <div style={S.statBarTrack}>
        <div
          style={{
            ...S.statBarFill,
            width: `${Math.max(0, Math.min(100, (value / max) * 100))}%`,
            background: color,
          }}
        />
      </div>
      <span style={S.statBarNum}>
        {Math.ceil(value)}/{max}
      </span>
    </div>
  );
}

const gold = "#e8a33d";
const S = {
  void: {
    minHeight: "100vh",
    background:
      "radial-gradient(ellipse at 50% 30%, #131720 0%, #0a0d12 55%, #050608 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "14px 10px 120px",
    boxSizing: "border-box",
    fontFamily: "Georgia, 'Times New Roman', serif",
    overflow: "hidden",
  },
  hud: { width: "100%", marginBottom: 8 },
  hudTitle: {
    color: gold,
    fontSize: 17,
    letterSpacing: "0.1em",
    textAlign: "center",
    marginBottom: 7,
    textShadow: "0 0 18px rgba(232,163,61,0.3)",
  },
  hudRegion: { color: "#8a9aa8", fontSize: 12, fontStyle: "italic" },
  statBar: { display: "flex", alignItems: "center", gap: 8, marginBottom: 5 },
  statBarLabel: {
    color: "#8a7a5c",
    fontSize: 10,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    width: 44,
    textAlign: "right",
  },
  statBarTrack: {
    flex: 1,
    height: 10,
    background: "rgba(0,0,0,0.5)",
    border: "1px solid rgba(232,163,61,0.25)",
    borderRadius: 999,
    overflow: "hidden",
  },
  statBarFill: { height: "100%", transition: "width 0.15s" },
  statBarNum: { color: "#b5a284", fontSize: 11, width: 64 },
  lytRow: { display: "flex", alignItems: "center", gap: 5, marginBottom: 6 },
  lytLabel: {
    color: "#8a7a5c",
    fontSize: 10,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    width: 44,
    textAlign: "right",
  },
  lytPip: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    border: "1px solid rgba(232,163,61,0.5)",
    background: "rgba(0,0,0,0.4)",
  },
  lytPipFull: { background: gold, boxShadow: "0 0 8px rgba(232,163,61,0.5)" },
  comboText: { color: "#8fd18a", fontSize: 11, marginLeft: 8, fontStyle: "italic" },
  playerBleed: { color: "#e86a5c", fontSize: 11, marginLeft: 10 },
  barHolder: { marginTop: 2 },
  viewport: {
    position: "relative",
    border: "1px solid rgba(232,163,61,0.35)",
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 0 40px rgba(140,170,200,0.06), inset 0 0 60px rgba(0,0,0,0.6)",
    maxWidth: "100%",
  },
  world: {
    position: "absolute",
    left: 0,
    top: 0,
    transition: "transform 0.12s linear",
  },
  ground: {
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(ellipse at 30% 20%, rgba(120,150,140,0.06), transparent 60%)," +
      "repeating-linear-gradient(0deg, #141a18 0, #141a18 47px, #182019 47px, #182019 48px)," +
      "repeating-linear-gradient(90deg, #141a18 0, #141a18 47px, #182019 47px, #182019 48px)",
  },
  leafDrift: {
    position: "absolute",
    width: 26,
    height: 10,
    borderRadius: "50%",
    background: "#7d6a44",
    transform: "rotate(24deg)",
    pointerEvents: "none",
  },
  hollow: {
    position: "absolute",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(190,210,190,0.10) 0%, rgba(190,210,190,0.04) 60%, transparent 75%)",
    border: "1px dashed rgba(190,210,190,0.25)",
    zIndex: 2,
  },
  shrine: {
    position: "absolute",
    left: "38%",
    top: "18%",
    fontSize: 26,
    opacity: 0.8,
    filter: "grayscale(0.4)",
  },
  bench: {
    position: "absolute",
    left: "30%",
    bottom: "24%",
    width: "40%",
    height: 7,
    background: "#5c4a33",
    borderRadius: 2,
    boxShadow: "0 6px 0 -3px #4a3b28",
  },
  hollowLabel: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: -18,
    textAlign: "center",
    color: "#9fb4a4",
    fontSize: 10,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
  },
  exit: { position: "absolute", zIndex: 3, width: TILE * 1.4 },
  exitArch: {
    width: "100%",
    height: TILE * 1.5,
    border: "3px solid #6b5a3a",
    borderBottom: "none",
    borderRadius: "50% 50% 0 0 / 70% 70% 0 0",
    background: "rgba(232,163,61,0.06)",
  },
  exitLabel: {
    textAlign: "center",
    color: "#b5a284",
    fontSize: 9.5,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    marginTop: 3,
  },
  fallenGate: { position: "absolute", zIndex: 3, width: TILE * 2.2 },
  gatePostL: {
    position: "absolute",
    left: 0,
    top: 0,
    width: 12,
    height: TILE * 1.9,
    background: "#3d3630",
    transform: "rotate(-7deg)",
    borderRadius: 3,
  },
  gatePostR: {
    position: "absolute",
    right: 0,
    top: 6,
    width: 12,
    height: TILE * 1.7,
    background: "#39332c",
    transform: "rotate(10deg)",
    borderRadius: 3,
  },
  gateMaw: {
    position: "absolute",
    left: "18%",
    top: "34%",
    width: "64%",
    height: "62%",
    background: "radial-gradient(ellipse at 50% 20%, #000 30%, #0d0a12 100%)",
    borderRadius: "50% 50% 8px 8px",
    boxShadow: "inset 0 0 20px #000, 0 0 26px rgba(140,120,200,0.18)",
  },
  gateLabel: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: -18,
    textAlign: "center",
    color: "#a794c9",
    fontSize: 10,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
  },
  birch: { position: "absolute", width: TILE, height: TILE * 2.2, zIndex: 4, pointerEvents: "none" },
  birchCanopy: {
    position: "absolute",
    left: "-30%",
    top: 0,
    width: "160%",
    height: "55%",
    background: "radial-gradient(ellipse, #5a6b52 0%, #46543f 70%)",
    borderRadius: "50%",
    opacity: 0.9,
  },
  birchTrunk: {
    position: "absolute",
    left: "40%",
    top: "42%",
    width: "20%",
    height: "58%",
    background: "#c9c4b8",
    borderRadius: 3,
  },
  birchScar: {
    position: "absolute",
    left: "10%",
    top: "28%",
    width: "80%",
    height: "8%",
    background: "#3a362e",
    borderRadius: 2,
  },
  node: {
    position: "absolute",
    zIndex: 5,
    fontSize: 20,
    color: "#a9c9a4",
    textShadow: "0 0 10px rgba(169,201,164,0.6)",
    transition: "opacity 0.4s",
    pointerEvents: "none",
  },
  nodeIron: { color: "#c9a46a", textShadow: "0 0 10px rgba(201,164,106,0.6)" },
  enemy: { position: "absolute", width: TILE, height: TILE * 1.2, zIndex: 8, transition: "left 0.16s linear, top 0.16s linear, filter 0.2s" },
  enemyTelegraph: { filter: "drop-shadow(0 0 10px #e86a5c) brightness(1.5)" },
  wolfBody: {
    position: "relative",
    width: "82%",
    height: "58%",
    top: "30%",
    left: "9%",
    background: "#6a6d72",
    borderRadius: "45% 55% 40% 40%",
    boxShadow: "inset -6px -4px 0 rgba(0,0,0,0.25)",
  },
  foxBody: {
    position: "relative",
    width: "70%",
    height: "50%",
    top: "38%",
    left: "15%",
    background: "#b0b6bd",
    borderRadius: "50% 60% 45% 45%",
    boxShadow: "inset -5px -3px 0 rgba(0,0,0,0.2)",
  },
  elkBody: {
    position: "relative",
    width: "92%",
    height: "66%",
    top: "26%",
    left: "4%",
    background: "#4a4238",
    borderRadius: "40% 50% 35% 35%",
    boxShadow: "inset -7px -5px 0 rgba(0,0,0,0.35), 0 0 14px rgba(120,80,140,0.25)",
  },
  eliteBody: {
    position: "relative",
    width: "100%",
    height: "72%",
    top: "22%",
    left: 0,
    background: "#3a3040",
    borderRadius: "40% 50% 35% 35%",
    boxShadow: "inset -8px -6px 0 rgba(0,0,0,0.4), 0 0 22px rgba(201,164,232,0.35)",
  },
  ears: {
    position: "absolute",
    left: "12%",
    top: "-26%",
    width: "30%",
    height: "40%",
    background: "inherit",
    clipPath: "polygon(0 100%, 25% 0, 50% 100%, 75% 0, 100% 100%)",
  },
  antlers: {
    position: "absolute",
    left: "6%",
    top: "-42%",
    width: "44%",
    height: "52%",
    borderLeft: "3px solid #d8cdb8",
    borderTop: "3px solid #d8cdb8",
    borderRadius: "60% 0 0 0",
    transform: "rotate(-12deg)",
  },
  antlersElite: {
    borderColor: "#c9a4e8",
    filter: "drop-shadow(0 0 6px rgba(201,164,232,0.6))",
    width: "58%",
    height: "66%",
    top: "-56%",
  },
  eyes: {
    position: "absolute",
    left: "14%",
    top: "26%",
    width: 5,
    height: 5,
    borderRadius: "50%",
    background: "#e86a5c",
    boxShadow: "10px 0 0 #e86a5c",
  },
  enemyHp: {
    position: "absolute",
    left: "-8%",
    top: "-14%",
    width: "116%",
    height: 5,
    background: "rgba(0,0,0,0.6)",
    border: "1px solid rgba(232,106,92,0.4)",
    borderRadius: 999,
    overflow: "hidden",
  },
  enemyHpFill: { height: "100%", background: "#e86a5c", transition: "width 0.12s" },
  eliteTag: {
    position: "absolute",
    left: "-40%",
    top: "-42%",
    width: "180%",
    textAlign: "center",
    color: "#c9a4e8",
    fontSize: 10,
    letterSpacing: "0.08em",
    whiteSpace: "nowrap",
    textShadow: "0 1px 3px #000",
  },
  bleedTag: {
    position: "absolute",
    left: "-16%",
    bottom: "-26%",
    color: "#e86a5c",
    fontSize: 10,
    whiteSpace: "nowrap",
  },
  downMark: { position: "absolute", color: "#6d5f47", fontSize: 20, zIndex: 7 },
  float: {
    position: "absolute",
    zIndex: 30,
    fontSize: 15,
    fontWeight: 700,
    textShadow: "0 1px 3px #000",
    transform: "translate(-50%, -100%)",
    transition: "top 0.9s ease-out, opacity 0.9s",
    pointerEvents: "none",
  },
  prompt: {
    position: "fixed",
    bottom: 130,
    left: "50%",
    transform: "translateX(-50%)",
    color: gold,
    fontSize: 13.5,
    background: "rgba(12,10,18,0.85)",
    border: "1px solid rgba(232,163,61,0.5)",
    borderRadius: 999,
    padding: "7px 18px",
    zIndex: 130,
    letterSpacing: "0.04em",
  },
  felt: {
    position: "fixed",
    top: "16%",
    left: "50%",
    transform: "translateX(-50%)",
    color: gold,
    fontSize: 15,
    fontStyle: "italic",
    textShadow: "0 0 16px rgba(232,163,61,0.5)",
    zIndex: 120,
    pointerEvents: "none",
    whiteSpace: "nowrap",
  },
  downVeil: {
    position: "fixed",
    inset: 0,
    background: "rgba(3,2,6,0.88)",
    zIndex: 300,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  downText: {
    color: "#e86a5c",
    fontSize: 26,
    letterSpacing: "0.14em",
    marginBottom: 10,
    textShadow: "0 0 30px rgba(232,106,92,0.5)",
  },
  downSub: { color: "#8a7a5c", fontSize: 13, fontStyle: "italic" },
  controls: { color: "#6d5f47", fontSize: 11, marginTop: 8, textAlign: "center" },
  back: {
    background: "transparent",
    border: "1px solid rgba(232,163,61,0.5)",
    color: gold,
    padding: "9px 20px",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.05em",
    marginTop: 10,
  },
  interactPad: {
    position: "fixed",
    right: 24,
    bottom: 170,
    width: 56,
    height: 56,
    borderRadius: "50%",
    border: "1px solid rgba(232,163,61,0.7)",
    background: "rgba(232,163,61,0.15)",
    color: gold,
    fontSize: 20,
    zIndex: 140,
    cursor: "pointer",
  },
};
